// Racional de cálculo de cada indicador da Análise de Estoque.
// Fonte única dos tooltips da UI — espelha ERPApi/docs/funcionalidades/estoque.md.

export interface MetricDescription {
  label: string;
  short: string;
  formula: string;
  rationale: string;
}

export const METRIC_DESCRIPTIONS: Record<string, MetricDescription> = {
  avg_daily_demand: {
    label: 'Demanda média diária',
    short: 'Média de unidades vendidas por dia (líquida de devoluções).',
    formula: 'Σ demanda líquida mensal na janela ÷ (meses × 30,44)',
    rationale:
      'A demanda usa a data do pedido de venda (quando o cliente quis o produto), excluindo pedidos cancelados e desmembrados. Brindes contam (consomem estoque) e devoluções são abatidas. É a base de todas as sugestões.',
  },
  current_stock_available_qty: {
    label: 'Estoque disponível',
    short: 'Físico menos o que já está reservado por pedidos de venda ainda sem baixa.',
    formula: 'Estoque físico − Σ itens de pedidos em status que reservam e sem baixa de estoque',
    rationale:
      'É o que realmente sobra para vender. Cobertura, excesso, ruptura e compra sugerida usam o disponível; o físico é o que está no galpão (inclui o que já foi vendido e ainda não saiu).',
  },
  coverage_days: {
    label: 'Cobertura (dias)',
    short: 'Quantos dias o estoque disponível atual dura no ritmo de venda previsto.',
    formula: 'Estoque disponível ÷ (demanda média diária × índice sazonal do período)',
    rationale:
      'Usa o disponível (físico − reservas de pedidos sem baixa), ajustado pela sazonalidade dos próximos ~30 dias: em meses de pico a mesma quantidade de estoque cobre menos dias. Cobertura vazia significa produto sem demanda na janela (não há como dividir por zero).',
  },
  qty_sold_monthly: {
    label: 'Quantidade vendida por mês',
    short: 'Unidades vendidas e devolvidas em cada mês dos últimos 24 meses.',
    formula: 'Σ quantidade dos itens de pedidos não cancelados, pelo mês da data do pedido',
    rationale:
      'É a série bruta de vendas (antes de descontar devoluções) que alimenta a demanda média e a sazonalidade. Brindes contam; meses sem linha são zero. A linha mostra quantos pedidos tiveram o produto.',
  },
  forecast_horizon_qty: {
    label: 'Demanda no horizonte',
    short: 'Unidades previstas até o fim do horizonte escolhido, contando o lead time.',
    formula: 'Demanda diária × índice sazonal do período × (lead time + meses × 30,44 dias)',
    rationale:
      'Para fornecedores sazonais, cobre a demanda dos próximos meses de uma vez. A quantidade sugerida desconta disponível e em pedido e soma o estoque de segurança; não é zerada por excesso.',
  },
  turnover_365d: {
    label: 'Giro anual (12m)',
    short: 'Quantas vezes o estoque "girou" nos últimos 12 meses.',
    formula: 'Demanda líquida de 365 dias ÷ estoque médio diário de 365 dias',
    rationale:
      'Giro alto = capital bem aproveitado; giro baixo = dinheiro parado em estoque. O estoque médio vem do snapshot diário (BIDomain.DailyProductStock). Compare produtos entre si e contra a meta da categoria.',
  },
  turnover_90d_annualized: {
    label: 'Giro anualizado (90d)',
    short: 'Giro dos últimos 90 dias projetado para o ano.',
    formula: '(Demanda líquida de 90 dias ÷ estoque médio de 90 dias) × (365 ÷ 90)',
    rationale:
      'Visão mais recente do ritmo do produto — reage mais rápido a mudanças de comportamento que o giro de 12 meses.',
  },
  turnover_cost_365d: {
    label: 'Giro a custo (12m)',
    short: 'Giro medido em valor (CMV / estoque médio a custo).',
    formula: 'CMV de 365 dias ÷ (estoque médio de 365 dias × custo unitário)',
    rationale:
      'Métrica financeira clássica de eficiência de estoque: usa o custo das mercadorias vendidas em vez de unidades.',
  },
  days_since_last_sale: {
    label: 'Dias sem venda',
    short: 'Dias desde a última venda do produto.',
    formula: 'Data atual − data do último pedido de venda válido',
    rationale:
      'Base da classificação de baixo giro: Normal < 90d ≤ Baixo giro < 180d ≤ Muito baixo giro < 365d ≤ Parado (limiares configuráveis). Produtos nunca vendidos ficam como "Sem histórico".',
  },
  slow_moving_class: {
    label: 'Classificação de giro',
    short: 'Bucket de aging do produto pelo tempo sem venda.',
    formula: 'Dias sem venda vs limiares (90/180/365 por padrão)',
    rationale:
      'Produtos "Muito baixo giro" e "Parado" têm a sugestão de compra zerada automaticamente — nunca compre estoque morto. Avalie liquidação, kits ou descontinuação.',
  },
  abc_class: {
    label: 'Curva ABC',
    short: 'Importância do produto pela receita (A = mais importante).',
    formula: 'Participação acumulada na receita: ≤80% = A, ≤95% = B, resto = C',
    rationale:
      'Princípio de Pareto: poucos produtos concentram a maior parte da receita. Itens A merecem nível de serviço alto e atenção diária; itens C toleram ruptura ocasional.',
  },
  xyz_class: {
    label: 'Curva XYZ',
    short: 'Previsibilidade da demanda (X = estável, Z = errática).',
    formula: 'Coeficiente de variação mensal: ≤0,5 = X, ≤1,0 = Y, resto = Z',
    rationale:
      'Combine com ABC: itens AX são fáceis de planejar (demanda alta e estável); itens AZ exigem mais estoque de segurança; itens CZ são candidatos a compra sob demanda.',
  },
  seasonal_index: {
    label: 'Índice sazonal',
    short: 'Quanto cada mês vende acima/abaixo da média (1,0 = neutro).',
    formula: 'Média de vendas do mês entre os anos ÷ média mensal geral (mín. 24 meses de histórico)',
    rationale:
      'Captura padrões como picos de fim de ano. As sugestões de compra usam o índice dos meses que o novo pedido vai cobrir — comprar para dezembro é diferente de comprar para abril. Limitado a [0,25; 4,0] para não amplificar ruído.',
  },
  lead_time_avg_days: {
    label: 'Lead time (dias)',
    short: 'Tempo médio entre o pedido de compra e o recebimento confirmado.',
    formula: 'Média de (data do recebimento − data do pedido de compra) dos recebimentos vinculados',
    rationale:
      'Calculado do histórico real: fallback produto → fornecedor padrão → média global → parâmetro. Amostras absurdas (<0 ou >365 dias) são descartadas. Quanto maior e mais variável o lead time, mais estoque de segurança é preciso.',
  },
  safety_stock_qty: {
    label: 'Estoque de segurança',
    short: 'Colchão contra variação de demanda e de prazo de entrega.',
    formula: 'SS = Z × √(LT×σd² + d̄²×σLT²); sem σLT: SS = Z×σd×√LT',
    rationale:
      'Z vem do nível de serviço configurado (95% → 1,645): a probabilidade desejada de NÃO faltar produto durante a reposição. σd é a variabilidade diária da demanda e σLT a variabilidade do lead time.',
  },
  reorder_point_qty: {
    label: 'Ponto de pedido',
    short: 'Nível de estoque em que um novo pedido de compra deve ser disparado.',
    formula: 'ROP = demanda diária prevista × lead time + estoque de segurança',
    rationale:
      'Cobre a demanda esperada durante o prazo de entrega do fornecedor, mais o colchão de segurança. É a base do estoque mínimo sugerido.',
  },
  suggested_min_qty: {
    label: 'Mínimo sugerido',
    short: 'Estoque mínimo recomendado para o cadastro do produto.',
    formula: '⌈ponto de pedido⌉',
    rationale:
      'Quando o estoque atinge este nível, é hora de comprar. Produtos sem histórico de venda não recebem sugestão (não há base estatística).',
  },
  suggested_max_qty: {
    label: 'Máximo sugerido',
    short: 'Estoque máximo recomendado (política de revisão periódica).',
    formula: '⌈demanda prevista × (lead time + ciclo de revisão) + estoque de segurança⌉',
    rationale:
      'Cobre a demanda até a chegada do PRÓXIMO pedido (lead time + ciclo de revisão entre compras), ajustada pela sazonalidade dos meses cobertos. Acima disso é capital parado.',
  },
  suggested_order_qty: {
    label: 'Compra sugerida',
    short: 'Quantidade sugerida para o próximo pedido de compra.',
    formula: 'máx(0, Máximo sugerido − (estoque disponível + quantidade em pedido))',
    rationale:
      'Repõe até o nível máximo, descontando o que já está disponível e o que já foi pedido aos fornecedores (POs abertas). Zerada para itens em excesso ou parados — nunca compre estoque morto.',
  },
  on_order_qty: {
    label: 'Em pedido',
    short: 'Quantidade já pedida a fornecedores e ainda não recebida.',
    formula: 'Σ quantidade de POs abertas (Em aberto/Em andamento) − já recebido',
    rationale:
      'Evita compra duplicada: o que está a caminho conta como estoque futuro na sugestão de compra.',
  },
  excess_stock: {
    label: 'Excesso de estoque',
    short: 'Estoque disponível acima da cobertura máxima configurada (padrão 180 dias).',
    formula: 'Excesso = estoque disponível − (demanda diária × cobertura máxima); valor = excesso × custo',
    rationale:
      'Mostra o capital imobilizado além do necessário. Produtos em excesso têm compra sugerida zerada e são candidatos a promoções.',
  },
  stock_purchase_cost_total: {
    label: 'Custo total do estoque',
    short: 'Quanto o estoque físico atual vale a preço de compra do fornecedor.',
    formula: 'Σ (estoque físico do produto × preço de compra do fornecedor vigente)',
    rationale:
      'É o capital efetivamente imobilizado em mercadoria, avaliado pelo preço praticado pelo fornecedor padrão de cada item (Financial.ProviderProductPrice). Produtos sem fornecedor/preço cadastrado entram como zero e não somam.',
  },
  stock_sale_value_total: {
    label: 'Valor de venda do estoque',
    short: 'Quanto o estoque físico atual representaria em faturamento a preço de tabela.',
    formula: 'Σ (estoque físico do produto × preço de venda cadastrado)',
    rationale:
      'Potencial de receita do estoque parado se vendido pelo preço de tabela do produto (ProductApi.Products.Price), sem descontos. A diferença para o custo total é a margem bruta teórica do estoque.',
  },
  stockout_risk: {
    label: 'Risco de ruptura',
    short: 'Cobertura menor que o tempo de reposição (lead time + ciclo).',
    formula: 'Cobertura < lead time + ciclo de revisão (com demanda > 0)',
    rationale:
      'Se a cobertura atual não alcança o tempo de repor, há risco real de faltar produto antes da próxima entrega. Priorize estes itens no planejamento de compras.',
  },
  stockout_days_365d: {
    label: 'Dias em ruptura (12m)',
    short: 'Dias com estoque zerado nos últimos 12 meses.',
    formula: 'Contagem de dias com snapshot diário ≤ 0',
    rationale:
      'Ruptura passada é venda perdida invisível: a demanda histórica desses dias foi zero porque não havia produto, não porque não havia procura. Produtos com muitos dias em ruptura têm demanda real MAIOR que a calculada.',
  },
  forecast_cycle_qty: {
    label: 'Demanda prevista no ciclo',
    short: 'Unidades previstas até a chegada do próximo pedido.',
    formula: 'Demanda diária prevista × (lead time + ciclo de revisão)',
    rationale: 'É a quantidade que o novo pedido de compra precisa cobrir, ajustada pela sazonalidade do período.',
  },
  confidence: {
    label: 'Confiança do cálculo',
    short: 'Qualidade dos dados por trás das sugestões deste produto.',
    formula: 'Alta: ≥12 meses de histórico e ≥80% de cobertura de snapshot; Média: ≥6 meses; Baixa: resto',
    rationale:
      'Produtos novos ou com lacunas no snapshot diário têm sugestões menos confiáveis — valide manualmente antes de aplicar.',
  },
};

export const getMetricDescription = (key: string): MetricDescription | undefined => METRIC_DESCRIPTIONS[key];
